import { createContext, useContext, useState, useEffect } from 'react';
import en from '../locales/en.json';

/**
 * AMAÇ: Dil yönetimi — t('auth.login') gibi basit çağrılarla metin döner
 * Çeviri eksikse İngilizce'ye, o da yoksa key'e düşer.
 * {{name}} gibi değişkenler otomatik replace edilir
 *
 * Performans: 7 dilin tamamını (~150 KB) her ziyaretçiye önden yüklemek yerine,
 * yalnızca İngilizce (evrensel yedek) statik olarak gömülü; diğer diller ilk
 * ihtiyaç duyulduğunda dynamic import ile ayrı bir chunk olarak indirilir ve
 * önbelleğe alınır. Bir dil henüz yüklenmemişken t() çağrılırsa, zaten var olan
 * "eksik çeviri → İngilizce'ye düş" mantığı devreye girer (kısa bir an İngilizce
 * görünüp yüklenince gerçek dile geçer) — ekstra bir "loading" hali gerekmez.
 */

const localeCache = { en };
const localePromises = {};

// 'en' burada yok — zaten yukarıda statik import edildi. Her case ayrı yazılır
// (şablon string yerine) ki Vite yalnızca gerçekten dinamik yüklenen 6 dil için
// chunk üretsin; aksi hâlde İngilizce hem statik hem dinamik chunk'ta iki kez yer alırdı.
function importLocale(code) {
  switch (code) {
    case 'tr': return import('../locales/tr.json');
    case 'es': return import('../locales/es.json');
    case 'de': return import('../locales/de.json');
    case 'fr': return import('../locales/fr.json');
    case 'pt': return import('../locales/pt.json');
    case 'ru': return import('../locales/ru.json');
    default:   return null;
  }
}

function loadLocale(code) {
  if (localeCache[code]) return Promise.resolve(localeCache[code]);
  if (localePromises[code]) return localePromises[code];

  const importer = importLocale(code);
  if (!importer) return Promise.resolve(localeCache.en); // bilinmeyen kod — sessizce İngilizce'ye düş

  localePromises[code] = importer.then((mod) => {
    localeCache[code] = mod.default;
    return mod.default;
  });
  return localePromises[code];
}

// Uygulama açılır açılmaz kalıcı/varsayılan dili indirmeye başla — React'in ilk
// render'ını bile beklemeden, mümkün olan en erken anda ağ isteği tetiklenir.
if (typeof window !== 'undefined') {
  loadLocale(localStorage.getItem('streak_lang') || 'tr');
}

/* Ayarlar'daki dil seçici bu listeden beslenir */
export const LANGUAGES = [
  { code: 'tr', flag: '🇹🇷', label: 'Türkçe' },
  { code: 'en', flag: '🇬🇧', label: 'English' },
  { code: 'es', flag: '🇪🇸', label: 'Español' },
  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
  { code: 'fr', flag: '🇫🇷', label: 'Français' },
  { code: 'pt', flag: '🇧🇷', label: 'Português' },
  { code: 'ru', flag: '🇷🇺', label: 'Русский' },
];

const I18nContext = createContext(null);

// Noktalı yolu verilen sözlükte çözer ('auth.login' → obj.auth.login)
const resolve = (dict, keys) => {
  let value = dict;
  for (const k of keys) {
    value = value?.[k];
    if (value === undefined) return undefined;
  }
  return value;
};

// t()'nin state'siz hali — belirli bir dille (React context/state beklemeden) çeviri çözer.
// Örn. LandingPage gibi dilini URL'den alan, ilk render'da yanlış dilin görünmesini
// (context'in henüz senkronize olmamış hali) istemeyen sayfalarda kullanılır.
// Dil henüz yüklenmediyse (bkz. loadLocale) İngilizce'ye düşer; LandingPage zaten
// mount'ta changeLang(lang) çağırıp yüklemeyi tetikliyor, yüklenince yeniden render olur.
export const translate = (lang, key, vars = {}) => {
  const keys = key.split('.');
  const value = resolve(localeCache[lang], keys) ?? resolve(localeCache.en, keys) ?? key;

  if (typeof value === 'string') {
    return Object.entries(vars).reduce(
      (str, [k, v]) => str.replaceAll(`{{${k}}}`, v),
      value
    );
  }

  return value;
};

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const l = localStorage.getItem('streak_lang') || 'tr';
    document.documentElement.lang = l; // CSS uppercase gibi dil-duyarlı davranışlar için
    return l;
  });
  // Dil chunk'ı yüklenip localeCache dolunca artar — hem context'i yeniden render eder
  // hem de LandingPage gibi ayrıca document.title/meta/canonical set eden sayfaların
  // "lang değişmedi ama veri artık hazır" durumunu fark edip o effect'i tekrar
  // çalıştırabilmesi için dışarı (context value'da) açılır.
  const [localeVersion, setLocaleVersion] = useState(0);

  useEffect(() => {
    if (localeCache[lang]) return;
    let cancelled = false;
    loadLocale(lang).then(() => { if (!cancelled) setLocaleVersion((n) => n + 1); });
    return () => { cancelled = true; };
  }, [lang]);

  const changeLang = (newLang) => {
    setLang(newLang);
    localStorage.setItem('streak_lang', newLang);
    document.documentElement.lang = newLang;
  };

  const t = (key, vars = {}) => translate(lang, key, vars);

  return (
    <I18nContext.Provider value={{ lang, changeLang, t, localeVersion }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
